// Tour-tag quick-reapply affordance (DESIGN §9). Binder pages upload roughly
// ordered by binder/tour, so neighbours usually share a tour — make reuse one
// tap during review:
//   1. The session-sticky LAST-APPLIED slug is the top, most prominent one-tap
//      "reapply" action (when set and not already on this item).
//   2. A row of one-tap chips for the other EXISTING tour slugs across the store,
//      excluding ones already on this item (and the last-applied one above).
// Tapping either adds the slug to this item's tourTags (which also refreshes the
// sticky last-applied value, via the parent's onApply). Multi-tag + free-text
// add still live in the adjacent <TagEditor>.

interface TourTagQuickApplyProps {
  /** Slugs already on this item (excluded from the suggestions). */
  current: string[];
  /** Distinct tour slugs across all loaded artifacts. */
  existing: string[];
  /** Session-sticky most-recently-applied slug (the prominent reapply). */
  lastApplied: string | null;
  /** Add a slug to this item's tourTags. */
  onApply: (slug: string) => void;
}

export function TourTagQuickApply({
  current,
  existing,
  lastApplied,
  onApply,
}: TourTagQuickApplyProps) {
  const has = (s: string) => current.includes(s);

  // The prominent reapply: only when set and not already on this item.
  const showReapply = !!lastApplied && !has(lastApplied);

  // Reuse chips: distinct existing slugs, minus ones already on the item and
  // minus the one already surfaced as the prominent reapply (no duplicate).
  const reuse = existing.filter((s) => !has(s) && s !== lastApplied);

  if (!showReapply && reuse.length === 0) return null;

  return (
    <div className="tour-quick">
      {showReapply && (
        <button
          type="button"
          className="tour-quick__reapply"
          onClick={() => onApply(lastApplied as string)}
          title={`Reapply the last tour you used: ${lastApplied}`}
        >
          <span className="tour-quick__reapply-label">Reapply</span>
          <span className="tour-quick__reapply-slug">{lastApplied}</span>
        </button>
      )}
      {reuse.length > 0 && (
        <div className="tour-quick__chips" role="group" aria-label="Reuse an existing tour tag">
          {reuse.map((slug) => (
            <button
              key={slug}
              type="button"
              className="tour-quick__chip"
              onClick={() => onApply(slug)}
              title={`Add tour: ${slug}`}
            >
              + {slug}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
